import { Icon, ICONS, isServer, useInitialScrollTo, useStickyBarHeight } from '@bob/library'
import { customHashScroll } from '@websrc/App/Helpers/customHashScroll'
import { usePath } from '@websrc/App/Hooks/usePath'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import {
  isMappedSectionLink,
  isMappedSubsection,
} from '@websrc/DataMappers/Guards/resourcePageSectionGuards'
import {
  isMappedLink,
  isMappedText,
} from '@websrc/DataMappers/Guards/resourcePageSectionItemGuards'
import { mapProductResourcesPage } from '@websrc/DataMappers/mapProductResourcePage'
import React, { Fragment, useRef, useState } from 'react'

import * as SC from './ProductResourceNav.style'

export interface ProductResourceNavProps {
  sections: ReturnType<typeof mapProductResourcesPage>['sections']
}

export function ProductResourceNav({ sections }: ProductResourceNavProps) {
  useInitialScrollTo(useRef(0))
  const path = usePath()
  const t = useStaticText()
  const [isOpen, setIsOpen] = useState(false)
  const activeHash = isServer ? '' : window.location.hash.substring(1)
  const totalStickyHeight = useStickyBarHeight()

  let activeSectionTitle = ''
  let activeSubsectionTitle = ''

  sections.forEach((s) => {
    if (s.url === path && s.caption) {
      activeSectionTitle = s.caption
    }

    s.items.forEach((sc) => {
      if (
        isMappedSubsection(sc) &&
        sc.subsection.url === path &&
        s.caption &&
        sc.subsection.caption
      ) {
        activeSectionTitle = s.caption
        activeSubsectionTitle = sc.subsection.caption
      }
    })
  })

  return (
    <SC.StyledList>
      <SC.MobileMenuItem
        aria-label={isOpen ? t.global.close : undefined}
        isOpen={isOpen}
        onClick={() => setIsOpen((s) => !s)}
      >
        {isOpen ? (
          <span /> // Needed for layout to the right
        ) : (
          <span>
            <strong>{activeSectionTitle}</strong>
            {activeSubsectionTitle ? ` - ${activeSubsectionTitle}` : ''}
          </span>
        )}
        <Icon icon={isOpen ? ICONS.CROSS : ICONS.ARROW_DOWN} size={20} />
      </SC.MobileMenuItem>
      {sections.map((s, sectionIndex) => {
        const isActiveSection =
          s.url === path ||
          s.items.find((sc) => isMappedSubsection(sc) && sc.subsection.url === path)

        return (
          <SC.SectionItem isOpen={isOpen} key={sectionIndex}>
            <SC.StyledAccordion
              button={(isActive) => (
                <>
                  <SC.AccordionLabel>{s.caption}</SC.AccordionLabel>
                  <Icon icon={isActive ? ICONS.ARROW_UP : ICONS.ARROW_DOWN} size={20} />
                </>
              )}
              initialState={Boolean(isActiveSection)}
            >
              <SC.BaseStyledList>
                {s.items.map((i, itemIndex) => (
                  <li key={itemIndex}>
                    {isMappedSectionLink(i) && (
                      <SC.StyledSpotLinkAnchor link={i.link} noStyles={true}>
                        <span>{i.link.cta_text}</span>
                        <Icon icon={ICONS.LINK_OUT} size={16} />
                      </SC.StyledSpotLinkAnchor>
                    )}
                    {isMappedSubsection(i) && (
                      <>
                        <SC.StyledLink activeClassName={'active'} to={i.subsection.url}>
                          {i.subsection.caption}
                        </SC.StyledLink>
                        {path === i.subsection.url && (
                          <SC.BaseStyledList>
                            {i.subsection.items.map((si, subItemIndex) => (
                              <Fragment key={subItemIndex}>
                                {isMappedLink(si) && (
                                  <li>
                                    <SC.StyledSpotLinkAnchor link={si.link} noStyles={true}>
                                      <span>{si.link.cta_text}</span>
                                      <Icon icon={ICONS.LINK_OUT} size={16} />
                                    </SC.StyledSpotLinkAnchor>
                                  </li>
                                )}
                                {isMappedText(si) && si.text.caption && (
                                  <li>
                                    <SC.StyledAnchor
                                      className={si.text.captionId === activeHash ? 'active' : ''}
                                      href={`#${si.text.captionId}`}
                                      onClick={(e) => {
                                        setIsOpen(false)
                                        customHashScroll(e, si.text.captionId, totalStickyHeight)
                                      }}
                                    >
                                      {si.text.caption}
                                    </SC.StyledAnchor>
                                  </li>
                                )}
                              </Fragment>
                            ))}
                          </SC.BaseStyledList>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </SC.BaseStyledList>
            </SC.StyledAccordion>
          </SC.SectionItem>
        )
      })}
    </SC.StyledList>
  )
}
